import { useEffect } from 'react';
import { Alert, Badge, Card, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'redux/store';

import { fetchWhereUsed, WhereUsedResult } from '../../../../../../../redux/slices/customer_node_definitionsSlice';

interface WhereUsedProps {
  nodeId: number | string;
}

const WhereUsed = ({ nodeId }: WhereUsedProps) => {
  const id = Number(nodeId);
  const dispatch = useAppDispatch();

  const data = useAppSelector((state) => state?.customerNodeDefinitions.whereUsedById?.[id] as WhereUsedResult | undefined);
  const loading = useAppSelector((state) => !!state?.customerNodeDefinitions.loading?.fetchWhereUsed);

  useEffect(() => {
    if (Number.isInteger(id) && id > 0) {
      dispatch(fetchWhereUsed(id));
    }
  }, [dispatch, id]);

  if (loading && !data) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" size="sm" /> Loading references…
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { flows, triggers, events, active_instances, integration_nodes } = data;

  const totalDefinitionRefs = flows.length + triggers.length + events.length + integration_nodes.length;
  const hasAnything = totalDefinitionRefs > 0 || active_instances.total > 0;

  if (!hasAnything) {
    return (
      <Alert variant="info" className="mt-2">
        This definition is not referenced anywhere.
      </Alert>
    );
  }

  return (
    <div className="mt-2">
      {flows.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            Flows <Badge bg="secondary">{flows.length}</Badge>
          </Card.Header>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Flow</th>
                <th>Flow node</th>
              </tr>
            </thead>
            <tbody>
              {flows.map((flow) => (
                <tr key={`${flow.node_definition_id}-${flow.flow_node_id}`}>
                  <td>{flow.zone_name}</td>
                  <td>
                    <Link to={`/node_editor/${flow.node_definition_id}/${encodeURIComponent(flow.name)}`}>{flow.name}</Link>
                  </td>
                  <td>{flow.flow_node_id}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {triggers.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            Triggers <Badge bg="secondary">{triggers.length}</Badge>
          </Card.Header>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Trigger</th>
                <th>Role</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {triggers.map((trigger) => (
                <tr key={`${trigger.trigger_id}-${trigger.role}`}>
                  <td>{trigger.zone_name}</td>
                  <td>
                    <Link to={`/automation/triggers?highlight=${trigger.trigger_id}`}>{trigger.name}</Link>
                  </td>
                  <td>
                    <Badge bg={trigger.role === 'watched' ? 'info' : 'warning'}>{trigger.role === 'watched' ? 'Watched' : 'Submit target'}</Badge>
                  </td>
                  <td>{trigger.action}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {events.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            Events <Badge bg="secondary">{events.length}</Badge>
          </Card.Header>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Event</th>
                <th>Type</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={`${event.event_id}-${event.link_type}`}>
                  <td>{event.zone_name}</td>
                  <td>
                    <Link to={`/automation/events?highlight=${event.event_id}`}>{event.name}</Link>
                  </td>
                  <td>{event.type}</td>
                  <td>
                    <Badge bg={event.link_type === 'wait' ? 'primary' : 'success'}>{event.link_type}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {integration_nodes.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            Integration nodes <Badge bg="secondary">{integration_nodes.length}</Badge>
          </Card.Header>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Integration</th>
                <th>Node</th>
              </tr>
            </thead>
            <tbody>
              {integration_nodes.map((node) => (
                <tr key={node.integration_node_id}>
                  <td>{node.zone_name}</td>
                  <td>{node.integration_name}</td>
                  <td>
                    <Link to={`/configuration/integrations`}>{node.name}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {active_instances.total > 0 && (
        <Card className="mb-3">
          <Card.Header>
            Instances <Badge bg="secondary">{active_instances.total}</Badge>
          </Card.Header>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(active_instances.by_status).map(([status, count]) => (
                <tr key={status}>
                  <td>{status}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default WhereUsed;
